import React, { useState, useEffect } from 'react';
import BuddyFlowEditor from './BuddyFlowEditor';
import { TipsTab, CategoriesTab, TransportTab, SettingsTab, ImagesTab } from './tabs';
import { AddTipModal, AddCategoryModal } from './modals';

const DarijaBuddyAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [activeTab, setActiveTab] = useState('tips');
  const [isLoading, setIsLoading] = useState(true);
  
  // Tips & Categories State
  const [tips, setTips] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAddTip, setShowAddTip] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingTip, setEditingTip] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Transport State
  const [transportCities, setTransportCities] = useState([]);
  const [transportRoutes, setTransportRoutes] = useState([]);
  const [showAddRoute, setShowAddRoute] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [newCity, setNewCity] = useState('');
  
  // Settings State
  const [settingsForm, setSettingsForm] = useState({
    botName: 'Darija Buddy',
    avatar: '🧕',
    avatarUrl: '',
    enabled: true,
    freeUserEnabled: true,
    initialDelaySeconds: 5,
    transportDisclaimer: 'Prices are approximate \u2014 verify with station.'
  });
  
  // Form states for modals
  const [newTip, setNewTip] = useState({
    category: '',
    content: '',
    contentDarija: '',
    translation: '',
    example: '',
    emoji: ''
  });
  
  const [newCategory, setNewCategory] = useState({
    name: '',
    label: '',
    emoji: '📚',
    color: 'blue'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    await Promise.all([fetchSettings(), fetchTips(), fetchCategories(), fetchTransportData()]);
    setIsLoading(false);
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/bot/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettingsForm(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const fetchTips = async () => {
    try {
      const url = selectedCategory ? `${API}/admin/buddy/tips?category=${selectedCategory}` : `${API}/admin/buddy/tips`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTips(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch tips:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/admin/buddy/categories`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchTransportData = async () => {
    try {
      const [citiesRes, routesRes] = await Promise.all([
        fetch(`${API}/admin/buddy/transport/cities`, { headers: { 'Authorization': `Bearer ${adminToken}` } }),
        fetch(`${API}/admin/buddy/transport/routes`, { headers: { 'Authorization': `Bearer ${adminToken}` } })
      ]);
      if (citiesRes.ok) {
        const data = await citiesRes.json();
        setTransportCities(data.cities || []);
      }
      if (routesRes.ok) {
        const data = await routesRes.json();
        setTransportRoutes(data.routes || []);
      }
    } catch (err) {
      console.error('Failed to fetch transport data:', err);
    }
  };

  const initializeBuddySystem = async () => {
    try {
      const res = await fetch(`${API}/admin/buddy/initialize`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(data.message, 'success');
        fetchTips();
        fetchCategories();
      }
    } catch (err) {
      showNotification('Failed to initialize', 'error');
    }
  };

  const saveSettings = async () => {
    try {
      const formData = new FormData();
      Object.keys(settingsForm).forEach(key => {
        if (settingsForm[key] !== null && settingsForm[key] !== undefined) {
          formData.append(key, settingsForm[key]);
        }
      });

      const res = await fetch(`${API}/admin/bot/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });

      if (res.ok) {
        showNotification('Settings saved!', 'success');
        fetchSettings();
      }
    } catch (err) {
      showNotification('Failed to save', 'error');
    }
  };

  // Tips CRUD
  const createTip = async () => {
    try {
      const formData = new FormData();
      formData.append('category', newTip.category);
      formData.append('content', newTip.content);
      if (newTip.contentDarija) formData.append('contentDarija', newTip.contentDarija);
      if (newTip.translation) formData.append('translation', newTip.translation);
      if (newTip.example) formData.append('example', newTip.example);
      if (newTip.emoji) formData.append('emoji', newTip.emoji);
      
      const res = await fetch(`${API}/admin/buddy/tips`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        showNotification('Tip created!', 'success');
        setShowAddTip(false);
        setNewTip({ category: '', content: '', contentDarija: '', translation: '', example: '', emoji: '' });
        fetchTips();
      }
    } catch (err) {
      showNotification('Failed to create tip', 'error');
    }
  };

  const updateTip = async (tipId, updates) => {
    try {
      const formData = new FormData();
      Object.keys(updates).forEach(key => {
        if (updates[key] !== undefined) formData.append(key, updates[key]);
      });
      
      const res = await fetch(`${API}/admin/buddy/tips/${tipId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        showNotification('Tip updated!', 'success');
        setEditingTip(null);
        fetchTips();
      }
    } catch (err) {
      showNotification('Failed to update', 'error');
    }
  };

  const deleteTip = async (tipId) => {
    if (!window.confirm('Delete this tip?')) return;
    try {
      await fetch(`${API}/admin/buddy/tips/${tipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      showNotification('Tip deleted', 'success');
      fetchTips();
    } catch (err) {
      showNotification('Failed to delete', 'error');
    }
  };

  // Categories CRUD
  const createCategory = async () => {
    try {
      const formData = new FormData();
      formData.append('name', newCategory.name);
      formData.append('label', newCategory.label);
      formData.append('emoji', newCategory.emoji);
      formData.append('color', newCategory.color);
      
      const res = await fetch(`${API}/admin/buddy/categories`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        showNotification('Category created!', 'success');
        setShowAddCategory(false);
        setNewCategory({ name: '', label: '', emoji: '📚', color: 'blue' });
        fetchCategories();
      }
    } catch (err) {
      showNotification('Failed to create category', 'error');
    }
  };

  const updateCategory = async (catId, updates) => {
    try {
      const formData = new FormData();
      Object.keys(updates).forEach(key => {
        if (updates[key] !== undefined) formData.append(key, updates[key]);
      });
      
      const res = await fetch(`${API}/admin/buddy/categories/${catId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        showNotification('Category updated!', 'success');
        setEditingCategory(null);
        fetchCategories();
      }
    } catch (err) {
      showNotification('Failed to update category', 'error');
    }
  };

  const deleteCategory = async (catId) => {
    const tipsInCategory = tips.filter(t => categories.find(c => c.id === catId)?.name === t.category).length;
    if (tipsInCategory > 0) {
      if (!window.confirm(`This category has ${tipsInCategory} tips. Delete anyway?`)) return;
    } else {
      if (!window.confirm('Delete this category?')) return;
    }
    try {
      await fetch(`${API}/admin/buddy/categories/${catId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      showNotification('Category deleted', 'success');
      fetchCategories();
    } catch (err) {
      showNotification('Failed to delete category', 'error');
    }
  };

  // Transport CRUD
  const seedTransportData = async () => {
    try {
      const res = await fetch(`${API}/admin/buddy/transport/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification('Transport data seeded successfully!', 'success');
        fetchTransportData();
      }
    } catch (err) {
      showNotification('Failed to seed transport data', 'error');
    }
  };

  const addCity = async () => {
    if (!newCity.trim()) return;
    try {
      const formData = new FormData();
      formData.append('name', newCity.trim());
      const res = await fetch(`${API}/admin/buddy/transport/cities`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        showNotification('City added!', 'success');
        setNewCity('');
        fetchTransportData();
      }
    } catch (err) {
      showNotification('Failed to add city', 'error');
    }
  };

  const deleteCity = async (cityId) => {
    if (!window.confirm('Delete this city?')) return;
    try {
      await fetch(`${API}/admin/buddy/transport/cities/${cityId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      showNotification('City deleted', 'success');
      fetchTransportData();
    } catch (err) {
      showNotification('Failed to delete city', 'error');
    }
  };

  const saveRoute = async (routeData) => {
    try {
      const formData = new FormData();
      formData.append('from_city', routeData.from_city);
      formData.append('to_city', routeData.to_city);
      formData.append('transports', JSON.stringify(routeData.transports));
      formData.append('is_active', routeData.is_active);
      
      const url = editingRoute ? `${API}/admin/buddy/transport/routes/${editingRoute.id}` : `${API}/admin/buddy/transport/routes`;
      const method = editingRoute ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        showNotification(editingRoute ? 'Route updated!' : 'Route created!', 'success');
        setShowAddRoute(false);
        setEditingRoute(null);
        fetchTransportData();
      }
    } catch (err) {
      showNotification('Failed to save route', 'error');
    }
  };

  const deleteRoute = async (routeId) => {
    if (!window.confirm('Delete this route?')) return;
    try {
      await fetch(`${API}/admin/buddy/transport/routes/${routeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      showNotification('Route deleted', 'success');
      fetchTransportData();
    } catch (err) {
      showNotification('Failed to delete route', 'error');
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-3xl overflow-hidden">
              {settingsForm.avatarUrl ? <img src={settingsForm.avatarUrl} alt="Bot" className="w-full h-full object-cover" /> : settingsForm.avatar}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{settingsForm.botName}</h2>
              <p className="text-white/70 text-sm">{tips.length} tips • {categories.length} categories</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={initializeBuddySystem} className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm rounded-lg">
              🚀 Initialize System
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        {['tips', 'flow', 'images', 'transport', 'categories', 'settings'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium capitalize transition-all ${
              activeTab === tab
                ? 'bg-purple-600 text-white'
                : isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab === 'tips' && '📚 '}{tab === 'flow' && '🌳 '}{tab === 'images' && '📸 '}{tab === 'transport' && '🚆 '}{tab === 'categories' && '🏷️ '}{tab === 'settings' && '⚙️ '}
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'tips' && (
        <TipsTab
          tips={tips}
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          editingTip={editingTip}
          setEditingTip={setEditingTip}
          setShowAddTip={setShowAddTip}
          fetchTips={fetchTips}
          updateTip={updateTip}
          deleteTip={deleteTip}
          isDarkMode={isDarkMode}
        />
      )}

      {activeTab === 'categories' && (
        <CategoriesTab
          categories={categories}
          tips={tips}
          setShowAddCategory={setShowAddCategory}
          editingCategory={editingCategory}
          setEditingCategory={setEditingCategory}
          updateCategory={updateCategory}
          deleteCategory={deleteCategory}
          isDarkMode={isDarkMode}
        />
      )}

      {activeTab === 'flow' && (
        <BuddyFlowEditor
          API={API}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          showNotification={showNotification}
          categories={categories}
          tips={tips}
        />
      )}

      {activeTab === 'images' && (
        <ImagesTab
          API={API}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          showNotification={showNotification}
        />
      )}

      {activeTab === 'transport' && (
        <TransportTab
          transportCities={transportCities}
          transportRoutes={transportRoutes}
          newCity={newCity}
          setNewCity={setNewCity}
          showAddRoute={showAddRoute}
          setShowAddRoute={setShowAddRoute}
          editingRoute={editingRoute}
          setEditingRoute={setEditingRoute}
          seedTransportData={seedTransportData}
          addCity={addCity}
          deleteCity={deleteCity}
          saveRoute={saveRoute}
          deleteRoute={deleteRoute}
          isDarkMode={isDarkMode}
          API={API}
          adminToken={adminToken}
        />
      )}

      {activeTab === 'settings' && (
        <SettingsTab
          settingsForm={settingsForm}
          setSettingsForm={setSettingsForm}
          saveSettings={saveSettings}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Modals */}
      {showAddTip && (
        <AddTipModal
          newTip={newTip}
          setNewTip={setNewTip}
          categories={categories}
          createTip={createTip}
          onClose={() => setShowAddTip(false)}
          isDarkMode={isDarkMode}
        />
      )}

      {showAddCategory && (
        <AddCategoryModal
          newCategory={newCategory}
          setNewCategory={setNewCategory}
          createCategory={createCategory}
          onClose={() => setShowAddCategory(false)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default DarijaBuddyAdmin;
